import { observable, makeObservable, action, runInAction } from "mobx";
import { deleteRequest, get, patch, post, put } from "../utils/axios-requests";
import { api } from "../constants/apis";

export default class MyDataUiStore {
  @observable error: string = "";
  @observable categories: Array<any> = [];
  @observable categoryInformation: any = {};
  @observable context: any = null;
  @observable loading: boolean = false;
  @observable userLanguages: Array<any> = [];
  @observable languages: Array<any> = [];
  @observable images: Array<any> = [];

  constructor() {
    makeObservable(this);
  }

  @action
  public initliazeContext = (context: any) => {
    this.context = context;
  };

  @action
  public setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  @action
  public setError = (err: string) => {
    this.error = err;
  };

  @action
  public fetchCategories = async () => {
    try {
      const res = await get(api.information.user, {
        userId: this.context.authenticatedUser.id,
      });

      runInAction(() => {
        this.categories = res.data.userCategories;
        this.userLanguages = res.data.infoLanguages;
      });

      return [res.data.infoLanguages, res.data.addedData];
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchCategoryInfo = async (category: string, fetch = false) => {
    try {
      if (this.categoryInformation[category] && !fetch) {
        return this.categoryInformation[category];
      }

      const res = await get(api.information.categoryInformation, {
        userId: this.context.authenticatedUser.id,
        categoryName: category,
      });

      this.categoryInformation[category] = res.data;
      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public getGeneralUserCategories = async () => {
    try {
      const res = await get(api.general.categories, {});

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public addUserCategories = async (
    newCategories: Array<String>,
    firstTime = false
  ) => {
    try {
      await post(api.categories.all, {
        newCategories,
        firstTime,
      });

      await this.fetchCategories();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public save_infos = async (infos: Array<any>, category: string) => {
    try {
      await patch(
        api.information.user,
        {
          infos,
        },
        {
          userId: this.context.authenticatedUser.id,
        }
      );

      return await this.fetchCategoryInfo(category, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  public getUserSectionsOptions = async (category: string) => {
    try {
      const res = await get(api.general.userSections, {
        userId: this.context.authenticatedUser.id,
        categoryName: category,
      });

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public deleteCategory = async (categoryId: string, category: string) => {
    try {
      await deleteRequest(api.categories.single, {
        categoryId: categoryId,
      });

      this.categoryInformation[category] = null;
      this.fetchCategories();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public addSection = async (category: string, name: string, type: string) => {
    try {
      await post(api.sections.all, {
        sectionName: name,
        sectionType: type,
        categoryName: category,
      });

      return await this.fetchCategoryInfo(category, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public renameSection = async (section: any, category: any, name: string) => {
    try {
      await post(
        api.sections.rename,
        {
          sectionName: section.name,
          category: category._id,
          newSectionName: name,
        },
        {
          sectionId: section._id,
        }
      );

      return await this.fetchCategoryInfo(category.name, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public renameSectionTitle = async (section: any, category: any, name: string) => {
    try {
      await post(
        api.sections.renameTitle,
        {
          sectionName: section.title,
          section: section._id,
          newSectionName: name,
        },
        {
          sectionId: section._id,
        }
      );

      return await this.fetchCategoryInfo(category.name, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public cloneSection = async (
    sectionId: string,
    lang: string,
    category: string
  ) => {
    try {
      await post(
        api.sections.clone,
        {
          lang,
        },
        {
          sectionId,
        }
      );

      return await this.fetchCategoryInfo(category, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public deleteSection = async (sectionId: string, category: string) => {
    try {
      await deleteRequest(api.sections.single, {
        userId: this.context.authenticatedUser.id,
        sectionId
      });

      return await this.fetchCategoryInfo(category, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public deleteLabel = async (labelId: string, category: string) => {
    try {
      await deleteRequest(api.information.single, {
        userId: this.context.authenticatedUser.id,
        labelId
      });

      return await this.fetchCategoryInfo(category, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public deleteAllSections = async (categoryName: string) => {
    try {
      await deleteRequest(api.sections.deleteAll, {
        userId: this.context.authenticatedUser.id,
        categoryName,
      });

      return await this.fetchCategoryInfo(categoryName, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
            "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchInfoSuggestions = async (
    infoType: string,
    countryId: any = null,
    stateId: any = null
  ) => {
    try {
      const res = await get(api.informationList.single, {
        infoType,
        countryId,
        stateId,
      });

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public fetchAllLanguages = async () => {
    try {
      const res = await get(api.languages.all);

      runInAction(() => {
        this.languages = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public addLang = async (lang: string) => {
    try {
      await post(
        api.languages.user,
        { infoLanguage: lang },
        {
          userId: this.context.authenticatedUser.id,
        }
      );

      this.categoryInformation = {};
      await this.fetchCategories();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public makeLangPrimary = async (lang: string) => {
    try {
      await put(
        api.languages.single,
        {},
        {
          userId: this.context.authenticatedUser.id,
          lang,
        }
      );

      await this.fetchCategories();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public switchLang = async (lang: string, langId: string, oldLang: string) => {
    try {
      await put(
        api.languages.user,
        {
          langId,
          language: lang,
          oldLang,
        },
        {
          userId: this.context.authenticatedUser.id,
        }
      );

      await this.fetchCategories();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action fetchImages = async () => {
    try {
      const res = await get(api.images.user, {
        userId: this.context.authenticatedUser.id,
      });

      runInAction(() => {
        this.images = res.data;
      });
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action deleteImg = async (imgId: string) => {
    try {
      await deleteRequest(api.images.single, {
        userId: this.context.authenticatedUser.id,
        imgId,
      });

      this.fetchImages();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action deleteLang = async (lang: string) => {
    try {
      await deleteRequest(api.languages.single, {
        userId: this.context.authenticatedUser.id,
        lang,
      });

      await this.fetchCategories();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action uploadImage = async (url: string, public_id: string) => {
    try {
      await post(
        api.images.user,
        { url, public_id },
        {
          userId: this.context.authenticatedUser.id,
        }
      );

      this.fetchImages();
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }
      return false;
    }
  };

  @action checkImages = async () => {
    try {
      const res = await get(api.images.checkImages, {
        userId: this.context.authenticatedUser.id,
      });

      return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public addCustomField = async (
    category: any,
    section: any,
    label: string
  ) => {
    try {
      await post(
        api.userLabels.user,
        {
          label,
          sectionName: section.name,
          sectionId: section._id,
          categoryName: category.name,
          categoryId: category._id,
        },
        {
          userId: this.context.authenticatedUser.id,
        }
      );

      return await this.fetchCategoryInfo(category.name, true);
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public addAvailability = async (availability: any, id: string) => {
    try {
      if (id) {
        return await put(api.availability.single, {
          availability
        }, {id});
      } else {
        return await post(api.availability.all, {
          availability
        }, {});
      }

    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };

  @action
  public getAvailability = async (id: string) => {
    try {
        const res =  await get(api.availability.single, {id});
        return res.data;
    } catch (error: any) {
      if (error.response) {
        this.setError(error.response.data);
      } else {
        this.setError(
          "Error: Can't complete this process right now. Please try again later"
        );
      }

      return false;
    }
  };
}
